'use client';
import React from 'react';

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import {
    faArrowUpRightFromSquare,
    faBell,
    faCheck,
    faCheckDouble,
    faChevronDown,
    faEnvelope,
    faQuestion,
    faRightFromBracket,
    faScrewdriverWrench,
    faThumbTack,
    faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon, Divider, Flex, Image, Indicator, Menu, Popover, Text, Tooltip } from '@mantine/core';

import NavMenu from '@components/layout/NavMenu/NavMenu';

import { useFrontNotifications } from '@hooks/useFrontNotifications';

import { useAppContext } from '@context/AppContext';
import { useHateoasContext } from '@context/HateoasContext';
import { useMainContext } from '@context/MainProvider';

import dayjs from '@utils/dayjs';
import myFetch from '@utils/myFetch';

import { downloadBinaryResponse } from '@/components/module/candidate-sheet/dt/helpers/downloadBinary';

const styles = {
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: 'var(--mantine-color-nli-yankees-blue-filled)',
        gap: 'var(--mantine-spacing-xl)',
        padding: 16,
        color: 'white',
        zIndex: 100,
    },
    iconButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 34,
        height: 34,
        padding: 0,
        border: 0,
        borderRadius: 999,
        background: 'transparent',
        color: 'inherit',
        cursor: 'pointer',
    },
    notificationIndicator: {
        display: 'inline-flex',
        overflow: 'visible',
    },
    notificationsDropdownContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--mantine-spacing-xs)',
    },
    notificationsScrollArea: {
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--mantine-spacing-xs)',
        maxHeight: 320,
        overflowY: 'auto',
        paddingRight: 4,
    },
    notificationPreviewItem: {
        display: 'grid',
        gridTemplateColumns: '3px minmax(0, 1fr) auto',
        gap: 'var(--mantine-spacing-xs)',
        alignItems: 'center',
        borderRadius: 'var(--mantine-radius-sm)',
        padding: 8,
    },
    notificationPreviewBar: {
        alignSelf: 'stretch',
        borderRadius: 999,
    },
    notificationPreviewBarUnread: {
        backgroundColor: 'var(--mantine-color-nli-error-filled)',
    },
    notificationPreviewContent: {
        appearance: 'none',
        border: 0,
        background: 'transparent',
        color: 'inherit',
        cursor: 'pointer',
        minWidth: 0,
        padding: 0,
        textAlign: 'left',
        width: '100%',
    },
    notificationArtifactPill: {
        appearance: 'none',
        border: '1px solid var(--mantine-color-nli-liberty-3)',
        borderRadius: 999,
        backgroundColor: 'var(--mantine-color-nli-liberty-light)',
        color: 'var(--mantine-color-nli-liberty-filled)',
        cursor: 'pointer',
        fontSize: 'var(--mantine-font-size-xs)',
        fontWeight: 600,
        maxWidth: '100%',
        overflow: 'hidden',
        padding: '2px 8px',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    profileMenuTrigger: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        maxWidth: 180,
        height: 34,
        padding: '0 10px',
        border: 0,
        borderRadius: 999,
        background: 'transparent',
        color: 'inherit',
        cursor: 'pointer',
    },
    profileAvatar: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24,
        borderRadius: 999,
        background: 'rgb(255 255 255 / 18%)',
        fontSize: 12,
    },
    profileName: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: 14,
        fontWeight: 600,
    },
    profileMenuChevron: {
        flexShrink: 0,
        fontSize: 11,
        opacity: 0.75,
    },
    profileMenuDropdown: {
        minWidth: 240,
    },
    userMenuIcon: {
        minWidth: 15,
    },
};

const getAvailableNotificationArtifacts = (notification) => {
    const metadata = notification?.metadata;
    if (!metadata || typeof metadata !== 'object') {
        return [];
    }

    const rawArtifacts = Array.isArray(metadata.artifacts)
        ? metadata.artifacts
        : (metadata.artifact ? [metadata.artifact] : []);

    return rawArtifacts
        .filter((artifact) => artifact && typeof artifact === 'object' && typeof artifact.downloadUrl === 'string')
        .filter((artifact) => {
            if (artifact.expired) {
                return false;
            }

            if (!artifact.expiresAt) {
                return true;
            }

            return new Date(artifact.expiresAt).getTime() > Date.now();
        })
        .map((artifact, index) => ({
            downloadUrl: artifact.downloadUrl,
            fileName: typeof artifact.fileName === 'string' && artifact.fileName ? artifact.fileName : `document-${index + 1}`,
        }));
};

export default function Header() {
    const searchParams = useSearchParams();
    const router = useRouter();
    const t = useTranslations();
    const { logout } = useMainContext();
    const [user] = useAppContext('user');
    const [iframe] = useAppContext('iframe');
    const { scopes } = useHateoasContext();
    const links = React.useMemo(() => scopes.app?.links || {}, [scopes.app?.links]);
    const { unreadCount, latest, markAsRead, markAsUnread, markAllAsRead } = useFrontNotifications();
    const [notificationsOpened, setNotificationsOpened] = React.useState(false);
    const [notificationsPinned, setNotificationsPinned] = React.useState(false);
    const notificationsTargetRef = React.useRef(null);
    const notificationsDropdownRef = React.useRef(null);
    const notificationsCloseTimeoutRef = React.useRef(null);

    const screenType = searchParams.get('screenType');
    const formatNotificationDate = (value) => dayjs(value).format('DD/MM/YYYY - HH:mm');
    const visibleNotifications = latest || [];

    const clearNotificationsCloseTimeout = React.useCallback(() => {
        if (!notificationsCloseTimeoutRef.current) {
            return;
        }

        window.clearTimeout(notificationsCloseTimeoutRef.current);
        notificationsCloseTimeoutRef.current = null;
    }, []);

    const openNotificationsPreview = React.useCallback(() => {
        clearNotificationsCloseTimeout();
        setNotificationsOpened(true);
    }, [clearNotificationsCloseTimeout]);

    const scheduleNotificationsClose = React.useCallback(() => {
        if (notificationsPinned) {
            return;
        }

        clearNotificationsCloseTimeout();
        notificationsCloseTimeoutRef.current = window.setTimeout(() => {
            setNotificationsOpened(false);
            notificationsCloseTimeoutRef.current = null;
        }, 150);
    }, [clearNotificationsCloseTimeout, notificationsPinned]);

    React.useEffect(() => () => {
        clearNotificationsCloseTimeout();
    }, [clearNotificationsCloseTimeout]);

    React.useEffect(() => {
        if (!notificationsPinned) {
            return undefined;
        }

        const handleDocumentMouseDown = (event) => {
            if (
                notificationsTargetRef.current?.contains(event.target)
                || notificationsDropdownRef.current?.contains(event.target)
            ) {
                return;
            }

            setNotificationsPinned(false);
            setNotificationsOpened(false);
        };

        document.addEventListener('mousedown', handleDocumentMouseDown);

        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown);
        };
    }, [notificationsPinned]);

    const openNotification = async (notification) => {
        try {
            if (!notification.isRead) {
                await markAsRead(notification.recipientId);
            }
        } catch {
            // Ignore read failures here; opening the notification should still work.
        } finally {
            setNotificationsPinned(false);
            setNotificationsOpened(false);
            router.push(notification.actionUrl || '/notifications');
        }
    };

    const markNotificationAsRead = async (event, notification) => {
        event.stopPropagation();
        try {
            if (notification.isRead) {
                await markAsUnread(notification.recipientId);
            } else {
                await markAsRead(notification.recipientId);
            }
        } catch {
            // Ignore toggle failures to keep the panel responsive.
        }
    };

    const downloadNotificationArtifact = async (event, notification, artifact) => {
        event.stopPropagation();
        event.preventDefault();

        try {
            if (!notification.isRead) {
                await markAsRead(notification.recipientId);
            }

            const response = await myFetch.get(artifact.downloadUrl, {}, true);
            downloadBinaryResponse(response, {
                fallbackFileName: artifact.fileName,
            });
        } catch {
            // Ignore download failures to avoid blocking notification interactions.
        }
    };

    const toggleNotificationsPinned = (event) => {
        event.stopPropagation();
        event.preventDefault();
        clearNotificationsCloseTimeout();
        setNotificationsPinned((current) => {
            const next = !current;
            setNotificationsOpened(true);
            return next;
        });
    };

    const markAllNotificationsAsRead = async (event) => {
        event.stopPropagation();
        event.preventDefault();

        if (!unreadCount) {
            return;
        }

        try {
            await markAllAsRead();
        } catch {
            return;
        }
    };

    if (screenType && screenType === 'integrated') return null;

    return (
        <header style={styles.header}>
            <Flex align="center">
                <Link href="/">
                    <Image
                        src="/images/logo.png"
                        alt="logo"
                        height={40}
                        loading="lazy"
                    />  
                </Link>
            </Flex>
            {
                user?._id && (
                    <>
                        <Flex flex={1}>
                            <NavMenu />
                        </Flex>
                        <Flex align="center" gap="xs">
                            <Popover
                                width={380}
                                position="bottom-end"
                                shadow="md"
                                opened={notificationsOpened || notificationsPinned}
                                onChange={setNotificationsOpened}
                            >
                                <Popover.Target>
                                    <div
                                        ref={notificationsTargetRef}
                                        onMouseEnter={openNotificationsPreview}
                                        onMouseLeave={scheduleNotificationsClose}
                                    >
                                        <button
                                            type="button"
                                            style={styles.iconButton}
                                            aria-label={t(notificationsPinned ? 'unpin-notifications' : 'pin-notifications')}
                                            onClick={toggleNotificationsPinned}
                                        >
                                            <Indicator
                                                inline
                                                disabled={!unreadCount}
                                                label={unreadCount > 9 ? '9+' : unreadCount}
                                                size={16}
                                                color="nli-error"
                                                style={styles.notificationIndicator}
                                            >
                                                <FontAwesomeIcon icon={faBell} />
                                            </Indicator>
                                        </button>
                                    </div>
                                </Popover.Target>
                                <Popover.Dropdown maw={380} w={380}>
                                    <div
                                        ref={notificationsDropdownRef}
                                        style={styles.notificationsDropdownContent}
                                        onMouseEnter={openNotificationsPreview}
                                        onMouseLeave={scheduleNotificationsClose}
                                    >
                                        <Flex align="center" justify="space-between" gap="sm">
                                            <Flex align="center" gap={4}>
                                                <Text
                                                    fw={700}
                                                    component={Link}
                                                    href='/notifications'
                                                    style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                                                >
                                                    {t('notifications')}
                                                </Text>
                                                <Tooltip label={t('open-notifications')} zIndex={1000}>
                                                    <ActionIcon
                                                        variant="subtle"
                                                        color="nli-liberty"
                                                        aria-label={t('open-notifications')}
                                                        component={Link}
                                                        href='/notifications'
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(event) => event.stopPropagation()}
                                                    >
                                                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            </Flex>
                                            <Flex gap={4}>
                                                <Tooltip label={t('mark-all-notifications-as-read')} zIndex={1000}>
                                                    <span>
                                                        <ActionIcon
                                                            variant={unreadCount ? 'light' : 'subtle'}
                                                            color={unreadCount ? 'nli-liberty' : 'gray'}
                                                            aria-label={t('mark-all-notifications-as-read')}
                                                            onClick={(event) => {
                                                                void markAllNotificationsAsRead(event);
                                                            }}
                                                            disabled={!unreadCount}
                                                        >
                                                            <FontAwesomeIcon icon={faCheckDouble} />
                                                        </ActionIcon>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip label={t(notificationsPinned ? 'unpin-notifications' : 'pin-notifications')} zIndex={1000}>
                                                    <ActionIcon
                                                        variant={notificationsPinned ? 'filled' : 'subtle'}
                                                        color="nli-liberty"
                                                        aria-label={t(notificationsPinned ? 'unpin-notifications' : 'pin-notifications')}
                                                        onClick={toggleNotificationsPinned}
                                                    >
                                                        <FontAwesomeIcon icon={faThumbTack} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            </Flex>
                                        </Flex>
                                        <Divider />
                                        <div style={styles.notificationsScrollArea}>
                                            {
                                                visibleNotifications.length > 0 ? visibleNotifications.map((notification) => {
                                                    const artifacts = getAvailableNotificationArtifacts(notification);

                                                    return (
                                                        <div
                                                            key={notification.recipientId}
                                                            style={styles.notificationPreviewItem}
                                                        >
                                                            <div
                                                                style={{
                                                                    ...styles.notificationPreviewBar,
                                                                    ...(!notification.isRead ? styles.notificationPreviewBarUnread : null),
                                                                }}
                                                            />
                                                            <div
                                                                role="button"
                                                                tabIndex={0}
                                                                style={styles.notificationPreviewContent}
                                                                onClick={() => {
                                                                    void openNotification(notification);
                                                                }}
                                                                onKeyDown={(event) => {
                                                                    if (event.currentTarget !== event.target) {
                                                                        return;
                                                                    }

                                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                                        event.preventDefault();
                                                                        void openNotification(notification);
                                                                    }
                                                                }}
                                                            >
                                                                <Text fw={notification.isRead ? 500 : 700} size="sm" lineClamp={1}>
                                                                    {notification.title}
                                                                </Text>
                                                                <Text size="sm" c="dimmed" lineClamp={2}>
                                                                    {notification.message}
                                                                </Text>
                                                                <Text size="xs" c="dimmed">
                                                                    {formatNotificationDate(notification.createdAt)}
                                                                </Text>
                                                                {
                                                                    artifacts.length > 0 ? (
                                                                        <Flex mt={6} gap={6} wrap="wrap">
                                                                            {artifacts.map((artifact) => (
                                                                                <Tooltip
                                                                                    key={`${notification.recipientId}-${artifact.downloadUrl}`}
                                                                                    label={t('download-notification-attachment', { fileName: artifact.fileName })}
                                                                                    zIndex={400}
                                                                                >
                                                                                    <button
                                                                                        type="button"
                                                                                        style={styles.notificationArtifactPill}
                                                                                        onClick={(event) => {
                                                                                            void downloadNotificationArtifact(event, notification, artifact);
                                                                                        }}
                                                                                    >
                                                                                        {artifact.fileName}
                                                                                    </button>
                                                                                </Tooltip>
                                                                            ))}
                                                                        </Flex>
                                                                    ) : null
                                                                }
                                                            </div>
                                                            <Tooltip label={notification.isRead ? t('mark-as-unread') : t('mark-as-read')} zIndex={400}>
                                                                <ActionIcon
                                                                    variant={notification.isRead ? 'subtle' : 'light'}
                                                                    color={notification.isRead ? 'gray' : 'nli-liberty'}
                                                                    aria-label={notification.isRead ? t('mark-as-unread') : t('mark-as-read')}
                                                                    onClick={(event) => {
                                                                        void markNotificationAsRead(event, notification);
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faCheck} />
                                                                </ActionIcon>
                                                            </Tooltip>
                                                        </div>
                                                    );
                                                }) : (
                                                    <Text size="sm" c="dimmed">
                                                        {t('no-new-notification')}
                                                    </Text>
                                                )
                                            }
                                        </div>
                                    </div>
                                </Popover.Dropdown>
                            </Popover>
                            <Menu position="bottom-end" width={260} shadow="md" withinPortal>
                                <Menu.Target>
                                    <button
                                        type="button"
                                        style={styles.profileMenuTrigger}
                                        aria-label={t('user')}
                                    >
                                        <span style={styles.profileAvatar}>
                                            <FontAwesomeIcon icon={faUser} />
                                        </span>
                                        {!iframe ? 
                                            <span style={styles.profileName}>
                                                {user.firstname}
                                                {' '}
                                                {user.lastname}
                                            </span> : null }
                                        <FontAwesomeIcon icon={faChevronDown} style={styles.profileMenuChevron} />
                                    </button>
                                </Menu.Target>
                                <Menu.Dropdown style={styles.profileMenuDropdown}>
                                    <Menu.Label>
                                        {t('connected-as')}
                                    </Menu.Label>
                                    <Text size="sm" fw={700} px="sm" pb="xs" lineClamp={1}>
                                        {user.firstname}
                                        {' '}
                                        {user.lastname}
                                    </Text>
                                    <Menu.Item
                                        component={Link}
                                        href="/user"
                                        leftSection={<FontAwesomeIcon
                                            icon={faUser}
                                            style={styles.userMenuIcon}
                                        />}
                                    >
                                        {t('header-user-menu-account')}
                                    </Menu.Item>
                                    {
                                        links.admin && (
                                            <Menu.Item
                                                component={Link}
                                                href="/admin"
                                                leftSection={<FontAwesomeIcon icon={faScrewdriverWrench} style={styles.userMenuIcon} />}
                                            >
                                                {t('header-user-menu-admin')}
                                            </Menu.Item>
                                        )
                                    }
                                    <Menu.Divider />
                                    <Menu.Item 
                                        component={Link} 
                                        href="/faq" 
                                        leftSection={<FontAwesomeIcon icon={faQuestion} style={styles.userMenuIcon} />}
                                    >
                                        {t('header-user-menu-faq')}
                                    </Menu.Item>
                                    <Menu.Item
                                        component="a"
                                        href="mailto:support@neylia.com"
                                        leftSection={<FontAwesomeIcon icon={faEnvelope} style={styles.userMenuIcon} />}
                                    >
                                        {t('header-user-menu-contact-support')}
                                        {iframe ? ' Neylia' : null}
                                    </Menu.Item>
                                    {
                                        !iframe && (
                                            <>
                                                <Menu.Divider />
                                                <Menu.Item
                                                    onClick={logout}
                                                    leftSection={<FontAwesomeIcon icon={faRightFromBracket} style={styles.userMenuIcon} />}
                                                >
                                                    {t('header-user-menu-logout')}
                                                </Menu.Item>
                                            </>
                                        )
                                    }
                                </Menu.Dropdown>
                            </Menu>
                        </Flex>
                    </>
                )
            }
        </header>
    );
}
